import {createReducer, on} from '@ngrx/store';
import {UserState} from './user.state';
import {
    deleteUser,
    deleteUserFailure,
    deleteUserSuccess, downloadFile, downloadFileFailure, downloadFileSuccess,
    fetchUser,
    fetchUserFailure,
    fetchUserSuccess,
    initializeNativeTryTerra,
    initializeNativeTryTerraFailure,
    initializeNativeTryTerraSuccess,
    fetchTryTerraUserInfo,
    fetchTryTerraUserInfoFailure,
    fetchTryTerraUserInfoSuccess,
    joinAgoraVideoCall,
    joinAgoraVideoCallFailure,
    joinAgoraVideoCallSuccess,
    leaveAgoraVideoCall, loadFiles, loadFilesFailure, loadFilesSuccess,
    login,
    loginAgoraChat,
    loginAgoraChatFailure,
    loginAgoraChatSuccess,
    loginFailure,
    loginOtp,
    loginOtpFailure,
    loginProvider,
    loginRecoveryCode,
    loginSuccess,
    logout,
    logoutAgoraChat,
    setLoading,
    toggleAgoraVideoCallIsMinimized,
    updateAgoraConversationList,
    updateAgoraConversationListFailure,
    updateAgoraConversationListSuccess,
    updateAgoraVideoCallIsMinimized,
    updateUser,
    updateUserAvatar,
    updateUserAvatarFailure,
    updateUserAvatarSuccess,
    updateUserBillingInfo,
    updateUserBillingInfoFailure,
    updateUserBillingInfoSuccess,
    updateUserFailure,
    updateUserSuccess,
    uploadFile, uploadFileFailure, uploadFileSuccess,
    triggerNativeTryTerraWebhook,
    triggerNativeTryTerraWebhookSuccess,
    triggerNativeTryTerraWebhookFailure,
} from './user.actions';

const initialState: UserState = {
    data: null,
    error: null,
    isLoading: false,
    tryTerra: {
        userInfo: null,
        isGettingUserInfo: false,
        userInfoError: null,
        nativeInitialized: false,
        isNativeInitializing: false,
        nativeInitializationError: null,
        nativeIsTriggeringWebhook: false,
        nativeTriggerWebhookError: null,
    },
    agora: {
        chat: {
            isLogging: false,
            isLogged: false,
            loginError: null,
            conversationList: [],
            conversationListIsLoading: false,
            conversationListError: null
        },
        videoCall: {
            isActive: false,
            isJoining: false,
            isMinimized: false,
            meetingId: null,
        }
    },
    fileShare: {
        isUploading: false,
        uploadError: null,
        isDownloading: false,
        downloadError: null,
        isLoading: false,
        loadError: null,
        files: [],
    }
};

export const userReducer = createReducer(
    initialState,
    on(login, (state) => ({
        ...state,
        error: null,
        isLoading: true,
        tryTerra: {
            ...state.tryTerra,
        },
        agora: {
            ...initialState.agora
        }
    })),
    on(setLoading, (state, {isLoading}) => ({
        ...state,
        error: null,
        isLoading,
    })),
    on(loginProvider, (state) => ({
        ...state,
        error: null,
        isLoading: true,
        tryTerra: {
            ...state.tryTerra,
        },
        agora: {
            ...initialState.agora
        }
    })),
    on(loginOtp, (state) => ({
        ...state,
        error: null,
        isLoading: true,
        tryTerra: {
            ...state.tryTerra,
        },
        agora: {
            ...initialState.agora
        }
    })),
    on(loginRecoveryCode, (state) => ({
        ...state,
        error: null,
        isLoading: true,
        tryTerra: {
            ...state.tryTerra,
        },
        agora: {
            ...initialState.agora
        }
    })),
    on(loginSuccess, (state, {data}) => ({
        ...state,
        data,
        error: null,
        isLoading: false,
    })),
    on(loginFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
        tryTerra: {
            ...state.tryTerra,
        },
        agora: {
            ...initialState.agora
        }
    })),
    on(loginOtpFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
        tryTerra: {
            ...state.tryTerra,
        },
        agora: {
            ...initialState.agora
        }
    })),
    on(loginAgoraChat, (state) => ({
        ...state,
        agora: {
            chat: {
                isLogging: true,
                isLogged: false,
                loginError: null,
                conversationList: [],
                conversationListIsLoading: false,
                conversationListError: null
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(loginAgoraChatSuccess, (state) => ({
        ...state,
        agora: {
            chat: {
                isLogging: false,
                isLogged: true,
                loginError: null,
                conversationList: [],
                conversationListIsLoading: false,
                conversationListError: null
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(loginAgoraChatFailure, (state, {error}) => ({
        ...state,
        agora: {
            chat: {
                isLogging: false,
                isLogged: false,
                loginError: error,
                conversationList: [],
                conversationListIsLoading: false,
                conversationListError: null
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(logoutAgoraChat, (state) => ({
        ...state,
        agora: {
            chat: {
                ...initialState.agora.chat,
                loginError: state.agora.chat.loginError,
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(fetchUser, (state) => ({
        ...state,
        error: null,
        isLoading: true,
    })),
    on(fetchUserSuccess, (state, {data}) => ({
        ...state,
        data,
        error: null,
        isLoading: false,
    })),
    on(fetchUserFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(logout, () => initialState),
    on(updateUser, (state, {data}) => ({
        ...state,
        error: null,
        isLoading: true,
    })),
    on(updateUserSuccess, (state, {data}) => ({
        ...state,
        data: state.data ? {
            ...state.data,
            ...data
        } : state.data,
        error: null,
        isLoading: false,
    })),
    on(updateUserFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(updateUserBillingInfo, (state, {data}) => ({
        ...state,
        error: null,
        isLoading: true,
    })),
    on(updateUserBillingInfoSuccess, (state, {data}) => ({
        ...state,
        data: state.data ? {
            ...state.data,
            ...data
        } : state.data,
        error: null,
        isLoading: false,
    })),
    on(updateUserBillingInfoFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(updateUserAvatar, (state) => ({
        ...state,
        error: null,
        isLoading: true,
    })),
    on(updateUserAvatarSuccess, (state) => ({
        ...state,
        error: null,
        isLoading: false,
    })),
    on(updateUserAvatarFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(deleteUser, (state) => ({
        ...state,
        error: null,
        isLoading: true,
    })),
    on(deleteUserSuccess, (state) => ({
        ...state,
        error: null,
        isLoading: false,
    })),
    on(deleteUserFailure, (state, {error}) => ({
        ...state,
        error,
        isLoading: false,
    })),
    on(fetchTryTerraUserInfo, (state) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            isGettingUserInfo: true,
        }
    })),
    on(fetchTryTerraUserInfoSuccess, (state, {userInfo}) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            userInfo: userInfo,
            isGettingUserInfo: false,
            userInfoError: null,
        }
    })),
    on(fetchTryTerraUserInfoFailure, (state, {error}) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            userInfo: null,
            isGettingUserInfo: false,
            userInfoError: error,
        }
    })),
    on(initializeNativeTryTerra, (state) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            nativeInitialized: false,
            isNativeInitializing: true,
            nativeInitializationError: null
        }
    })),
    on(initializeNativeTryTerraSuccess, (state) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            nativeInitialized: true,
            isNativeInitializing: false,
            nativeInitializationError: null
        }
    })),
    on(initializeNativeTryTerraFailure, (state, {error}) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            nativeInitialized: false,
            isNativeInitializing: false,
            nativeInitializationError: error
        }
    })),
    on(triggerNativeTryTerraWebhook, (state) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            nativeIsTriggeringWebhook: true,
            nativeTriggerWebhookError: null
        }
    })),
    on(triggerNativeTryTerraWebhookSuccess, (state) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            nativeIsTriggeringWebhook: false,
            nativeTriggerWebhookError: null
        }
    })),
    on(triggerNativeTryTerraWebhookFailure, (state, {error}) => ({
        ...state,
        tryTerra: {
            ...state.tryTerra,
            nativeIsTriggeringWebhook: false,
            nativeTriggerWebhookError: error
        }
    })),
    on(updateAgoraConversationList, (state) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
                conversationList: [...state.agora.chat.conversationList],
                conversationListIsLoading: true,
                conversationListError: null
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(updateAgoraConversationListSuccess, (state, {conversationList}) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
                conversationList: conversationList,
                conversationListIsLoading: false,
                conversationListError: null
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(updateAgoraConversationListFailure, (state, {error}) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
                conversationList: [],
                conversationListIsLoading: false,
                conversationListError: error
            },
            videoCall: {
                ...state.agora.videoCall
            },
        }
    })),
    on(updateAgoraVideoCallIsMinimized, (state, {isMinimized}) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
            },
            videoCall: {
                ...state.agora.videoCall,
                isMinimized: isMinimized
            },
        }
    })),
    on(toggleAgoraVideoCallIsMinimized, (state) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
            },
            videoCall: {
                ...state.agora.videoCall,
                isMinimized: !state.agora.videoCall.isMinimized
            },
        }
    })),
    on(joinAgoraVideoCall, (state, {meetingId}) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
            },
            videoCall: {
                ...state.agora.videoCall,
                isActive: false,
                isJoining: true,
                meetingId: meetingId,
            },
        }
    })),
    on(joinAgoraVideoCallSuccess, (state) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
            },
            videoCall: {
                ...state.agora.videoCall,
                isActive: true,
                isJoining: false,
            },
        }
    })),
    on(joinAgoraVideoCallFailure, (state) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
            },
            videoCall: {
                ...state.agora.videoCall,
                isActive: false,
                isMinimized: false,
                isJoining: false,
            },
        }
    })),
    on(leaveAgoraVideoCall, (state) => ({
        ...state,
        agora: {
            chat: {
                ...state.agora.chat,
            },
            videoCall: {
                ...state.agora.videoCall,
                isActive: false,
                isMinimized: false,
                isJoining: false,
                videoCallTracks: null,
                meetingId: null,
            },
        }
    })),
    on(loadFiles, (state) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isLoading: true,
            loadError: null,
        }
    })),
    on(loadFilesSuccess, (state, {files}) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isLoading: false,
            loadError: null,
            files: files,
        }
    })),
    on(loadFilesFailure, (state, {error}) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isLoading: false,
            loadError: error,
            files: [],
        }
    })),
    on(uploadFile, (state) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isUploading: true,
            uploadError: null,
        }
    })),
    on(uploadFileSuccess, (state) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isUploading: false,
            uploadError: null,
        }
    })),
    on(uploadFileFailure, (state, {error}) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isUploading: false,
            uploadError: error,
        }
    })),
    on(downloadFile, (state) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isDownloading: true,
            downloadError: null,
        }
    })),
    on(downloadFileSuccess, (state) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isDownloading: false,
            downloadError: null,
        }
    })),
    on(downloadFileFailure, (state, {error}) => ({
        ...state,
        fileShare: {
            ...state.fileShare,
            isDownloading: false,
            downloadError: error,
        }
    })),
);
